
import React from 'react';

import Joi from 'joi';
import Form from '../../common/form';
import Button from '../../themed/button';
import Conditional from '../../common/conditional';

import { func, object } from 'prop-types';
import { getFileUploadTypes, getValueRange } from '../../../utils/utilities';

import toastService from '../../../services/toastService';

class LRUTDataItemForm extends Form {
	state = {
		data: {
            shotNumber: '',
            feature: '',
            location: '',
            ecl: '',
            length: 0,
            extent: 0,
            class: '',
            scanDirection: '',
            notes: '',
            aScan: [],
            cScan: [],
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
        id: Joi.number().optional().label('ID'),
        shotNumber: Joi.string().required().label('Shot Number'),
        feature: Joi.string().required().label('Feature'),
        location: Joi.string().required().label('Location (M)'),
        ecl: Joi.string().required().label('ECL'),
        length: Joi.number().positive().required().label('Length (mm)'),
        extent: Joi.number().positive().required().label('Extent (deg)'),
        class: Joi.string().required().label('Class'),
        scanDirection: Joi.string().required().label('Scan Direction'),
        notes: Joi.string().required().label('Notes'),
        aScan: Joi.array().min(1).max(1).required().label('A Scan'),
        cScan: Joi.array().min(1).max(1).required().label('C Scan'),
	};

	static propTypes = {
        initialLRUTItem: object,
        onDeleteItem: func,
		onSave: func,
	};

    componentDidMount(): void {
        if (this.props.initialLRUTItem) {
            this.setState({ data: this.props.initialLRUTItem });
        }
    }

	doSubmit = (): void => {
		this.props.onSave(this.state.data);
        if (!this.props.initialLRUTItem) this.resetData();
        toastService.success('Changes saved!')
	};

    resetData = () => {
        this.setState({
            data: {
                shotNumber: '',
                feature: '',
                location: '',
                ecl: '',
                length: 0,
                extent: 0,
                class: '',
                scanDirection: '',
                notes: '',
                aScan: [],
                cScan: [],
            },
        });
    };

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<h3 className="form-title">LRUT Data Item</h3>

                <div className="container">
                    <div className="row">
                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'shotNumber',
                                label: 'Shot Number',
                                placeholder: 'Enter Shot Number',
                            })}
                        </div>
                        
                        <div className="col">
                            {this.renderSelect({
                                options: getFeatures(),
                                name: 'feature',
                                label: 'Feature',
                                placeholder: 'Enter Feature',
                            })}
                        </div>

                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'location',
                                label: 'Location (M)',
                                placeholder: 'Enter Location',
                            })}
                        </div>
                        
                        <div className="col">
                            {this.renderSelect({
                                options: ECL,
                                name: 'ecl',
                                label: 'ECL',
                                placeholder: 'Enter ECL',
                            })}
                        </div>
                    </div>

                    <div className="divider my-4"></div>
                    
                    <div className="row">
                        <div className="col">
                            {this.renderSelect({
                                options: LENGTH,
                                name: 'length',
                                label: 'Length (MM)',
                                placeholder: 'Enter Length',
                            })}
                        </div>
                        
                        <div className="col">
                            {this.renderSelect({
                                options: DEGREE,
                                name: 'extent',
                                label: 'Extent (Deg)',
                                placeholder: 'Enter Extent (Deg)',
                            })}
                        </div>
                        
                        <div className="col">
                            {this.renderSelect({
                                options: CLASSES,
                                name: 'class',
                                label: 'Class',
                                placeholder: 'Enter Class',
                            })}
                        </div>
                       
                        <div className="col">
                            {this.renderSelect({
                                name: 'scanDirection',
                                label: 'Scan Direction',
                                placeholder: 'Select Scan Direction',
                                options: DIRECTIONS,
                            })}
                        </div>

                    </div>

                    <div className="divider my-4"></div>

                    <div className="row">
                        <div className="col">
                            {this.renderFileInput({
                                max: 1,
                                multiple: false,
                                name: 'aScan',
                                accept: 'image/*',
                                label: 'A Scan Image',
                                sublabel: 'Upload A Scan Image',
                                types: getFileUploadTypes(),
                            })}
                        </div>

                        <div className="col">
                            {this.renderFileInput({
                                max: 1,
                                multiple: false,
                                name: 'cScan',
                                accept: 'image/*',
                                label: 'C Scan Image',
                                sublabel: 'Upload C Scan Image',
                                types: getFileUploadTypes(),
                            })}
                        </div>
                    </div>

                    <div className="divider my-4"></div>

                    <div className="row">
                        <div className="col">
                            {this.renderTextEditor({
                                name: 'notes',
                                label: 'Notes',
                                placeholder: 'Enter Notes',
                            })}
                        </div>
                    </div>
                </div>

                <div className="divider my-4"></div>

                <div className="d-flex justify-content-end">
                    <Conditional isVisible={!!this.props.onDeleteItem}>
                        <div className="me-3">
                            <Button
                                onClick={this.props.onDeleteItem} 
                                icon="trash"
                                type="button"
                                label="Delete" 
                                style={{ backgroundColor: 'red' }} 
                            />
                        </div>
                    </Conditional>

				    {this.renderButton('Save')}
                </div>
			</form>
		);
	}
}

function getFeatures() {
    const positiveFeatures = getValueRange(1, 31).map((value) => ({ id: `F${value}`, name: `F${value}` }));
    const negativeFeatures = positiveFeatures.map((feature) => ({ id: `-${feature.id}`, name: `-${feature.name}` }));

    return positiveFeatures.concat(negativeFeatures);
}

const ECL = getValueRange(0, 51).map((value) => ({ id: value, name: value }));
const LENGTH = getValueRange(0, 501).map((value) => ({ id: value, name: value }));
const DEGREE = getValueRange(0, 361).map((value) => ({ id: value, name: value }));
const CLASSES = ['OK', 'End', 'Minor', 'Medium', 'Major', 'Severe', 'Weld', 'Support', 'Saddle', 'Clamp', 'Bend', 'Flange', 'T', 'Vent', 'Corrosion', 'Other'].map((classItem) => ({ id: classItem, name: classItem }));
const DIRECTIONS = ['Flow Direction', 'Flow Opposite Direction'].map((classItem) => ({ id: classItem, name: classItem }));



export default LRUTDataItemForm;
